import { useState, useRef, useEffect } from "react";
import { useCart } from "../context/CartContext";
import styles from "./Checkout.module.css";

const WEBXPAY_API = "https://ratnapura-gems-api.vercel.app/api/encrypt";
const MINTPAY_API = "https://ratnapura-gems-api.vercel.app/api/mintpay";
const NOTIFY_API = "https://ratnapura-gems-api.vercel.app/api/notify";
const VOUCHER_API = "https://ratnapura-gems-api.vercel.app/api/vouchers";
const APPLY_VOUCHER_API = "https://ratnapura-gems-api.vercel.app/api/apply-voucher";


const MintpayLogo = ({ height = 16 }) => (
  <img src="/mintpay-logo.webp" alt="Mintpay" style={{ height, width: "auto", verticalAlign: "middle" }} />
);

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const webxpayFormRef = useRef(null);
  const mintpayFormRef = useRef(null);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "",
    phone: "", address: "", city: "",
    postcode: "", instructions: "",
  });
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);

  // Gift voucher: fixed amount off, tied to one product, single use.
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);      // { code, discount }
  const [voucherError, setVoucherError] = useState("");
  const [checkingVoucher, setCheckingVoucher] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("webxpay");
  const [webxpayData, setWebxpayData] = useState(null);
  const [mintpayData, setMintpayData] = useState(null);


  // Reset paying state when user comes back to this page
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setPaying(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.address.trim()) e.address = "Required";
    if (!form.city.trim()) e.city = "Required";
    return e;
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  // A voucher is tied to one product, so it only applies if that product
  // is actually in the cart. Never let the discount exceed the total.
  const discount = voucher
    ? Math.min(voucher.discount, total)
    : 0;
  const payableTotal = Math.max(total - discount, 0);
  const installment = Math.ceil(payableTotal / 3).toLocaleString();

  // Mintpay is unavailable once a voucher is applied.
  //
  // A voucher is marked used only when the gateway confirms the payment.
  // WebXPay calls our server back, so that confirmation is reliable.
  // Mintpay has no server callback — it just redirects the browser — so a
  // voucher spent on a Mintpay order would stay unused and could be
  // redeemed again. Rather than trust the redirect, we take Mintpay off
  // the table for voucher orders. The server enforces this too; this is
  // only so the customer never picks an option that will be refused.
  const mintpayAvailable = !voucher;

  const applyVoucher = async () => {
    const code = voucherInput.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!code) {
      setVoucherError("Please enter a voucher code.");
      return;
    }
    setCheckingVoucher(true);
    setVoucherError("");
    try {
      // Vouchers apply to any product, so one check is enough.
      const r = await fetch(`${VOUCHER_API}?validate=${encodeURIComponent(code)}`);
      const data = await r.json();

      if (data.valid) {
        setVoucher({ code: data.code, discount: data.discount });
        setVoucherInput("");
        // Mintpay is about to disappear from the list, so move the
        // selection off it rather than leaving an invisible choice active.
        setPaymentMethod("webxpay");
      } else {
        setVoucherError(data.reason || "This voucher code isn't valid.");
      }
    } catch {
      setVoucherError("Couldn't check that code. Please try again.");
    }
    setCheckingVoucher(false);
  };

  const removeVoucher = () => {
    setVoucher(null);
    setVoucherError("");
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (items.length === 0) { alert("Your cart is empty."); return; }

    setPaying(true);
    const orderId = "RG-" + Date.now();

    // Record which voucher was used against this order. It is only
    // marked used once the gateway confirms payment succeeded, so an
    // abandoned or failed payment leaves it usable.
    if (voucher) {
      try {
        const vr = await fetch(APPLY_VOUCHER_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId,
            voucherCode: voucher.code,
          }),
        });
        if (!vr.ok) {
          const err = await vr.json();
          alert(err.error || "That voucher is no longer valid.");
          setVoucher(null);
          setPaying(false);
          return;
        }
      } catch {
        alert("Couldn't verify your voucher. Please try again.");
        setPaying(false);
        return;
      }
    }

    // Send notification immediately when payment is attempted
    fetch(NOTIFY_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        orderId, total: payableTotal, paymentMethod,
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, phone: form.phone,
        address: form.address, city: form.city,
        postcode: form.postcode, instructions: form.instructions,
        items,
        voucherCode: voucher?.code || null,
        voucherDiscount: discount || 0,
      }),
    }).catch(console.error);

    try {
      if (paymentMethod === "webxpay") {
        const response = await fetch(WEBXPAY_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId, amount: payableTotal,
            firstName: form.firstName, lastName: form.lastName,
            email: form.email, phone: form.phone,
            address: form.address, city: form.city,
            instructions: form.instructions,
            // The server re-prices this cart and rejects the request if
            // our total doesn't match, so it must be sent.
            items,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || "WebXPay API failed");
        }
        const data = await response.json();
        setWebxpayData(data);
        setTimeout(() => webxpayFormRef.current?.submit(), 300);

      } else if (paymentMethod === "mintpay") {
        // Belt and braces: the option is hidden once a voucher is applied,
        // but never send a voucher order to a gateway that can't confirm it.
        if (voucher) {
          throw new Error("Installment payments aren't available on gift voucher orders.");
        }
        const response = await fetch(MINTPAY_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderId, amount: payableTotal,
            firstName: form.firstName, lastName: form.lastName,
            email: form.email, phone: form.phone,
            address: form.address, city: form.city,
            postcode: form.postcode, items,
          }),
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.mintpayMessage || errData.error || "Mintpay API failed");
        }
        const data = await response.json();
        setMintpayData(data);
        setTimeout(() => mintpayFormRef.current?.submit(), 300);
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment error: " + error.message);
      setPaying(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <h2>Your cart is empty</h2>
          <button onClick={() => window.location.href = "/"} className={styles.backHomeBtn}>
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Hidden WebXPay form */}
      {webxpayData && (
        <form ref={webxpayFormRef} method="POST" action={webxpayData.webxpayUrl} style={{ display: "none" }}>
          {Object.entries(webxpayData.formData).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}
        </form>
      )}

      {/* Hidden Mintpay form */}
      {mintpayData && (
        <form ref={mintpayFormRef} method="POST" action={mintpayData.redirectUrl} style={{ display: "none" }}>
          <input type="hidden" name="purchase_id" value={mintpayData.purchaseId} />
        </form>
      )}

      <div className={styles.inner}>
        <p className={styles.eyebrow}>Almost There</p>
        <h1 className={styles.title}>Checkout</h1>
        <div className={styles.divider} />

        <div className={styles.grid}>
          {/* Form */}
          <form className={styles.form} onSubmit={handlePayment}>
            <h2 className={styles.sectionTitle}>Delivery Information</h2>

            <div className={styles.row}>
              <div className={styles.field}>
                <label>First Name *</label>
                <input name="firstName" value={form.firstName} onChange={handleChange}
                  placeholder="First Name" className={errors.firstName ? styles.inputError : ""} />
                {errors.firstName && <span className={styles.error}>{errors.firstName}</span>}
              </div>
              <div className={styles.field}>
                <label>Last Name *</label>
                <input name="lastName" value={form.lastName} onChange={handleChange}
                  placeholder="Last Name" className={errors.lastName ? styles.inputError : ""} />
                {errors.lastName && <span className={styles.error}>{errors.lastName}</span>}
              </div>
            </div>

            <div className={styles.field}>
              <label>Email Address *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange}
                placeholder="you@example.com" className={errors.email ? styles.inputError : ""} />
              {errors.email && <span className={styles.error}>{errors.email}</span>}
            </div>

            <div className={styles.field}>
              <label>Phone Number *</label>
              <input name="phone" value={form.phone} onChange={handleChange}
                placeholder="07X XXX XXXX" className={errors.phone ? styles.inputError : ""} />
              {errors.phone && <span className={styles.error}>{errors.phone}</span>}
            </div>

            <div className={styles.field}>
              <label>Delivery Address *</label>
              <input name="address" value={form.address} onChange={handleChange}
                placeholder="No. 12, Main Street" className={errors.address ? styles.inputError : ""} />
              {errors.address && <span className={styles.error}>{errors.address}</span>}
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label>City *</label>
                <input name="city" value={form.city} onChange={handleChange}
                  placeholder="Colombo" className={errors.city ? styles.inputError : ""} />
                {errors.city && <span className={styles.error}>{errors.city}</span>}
              </div>
              <div className={styles.field}>
                <label>Postcode</label>
                <input name="postcode" value={form.postcode} onChange={handleChange} placeholder="00100" />
              </div>
            </div>

            <div className={styles.field}>
              <label>Special Instructions (optional)</label>
              <textarea name="instructions" value={form.instructions} onChange={handleChange}
                placeholder="Ring size, gift wrapping, special notes..." rows={3} />
            </div>

            {/* Payment Method Selector */}
            <h2 className={styles.sectionTitle} style={{ marginTop: 8 }}>Payment Method</h2>
            <div className={styles.paymentMethods}>

              {/* WebXPay */}
              <div
                className={`${styles.paymentOption} ${paymentMethod === "webxpay" ? styles.paymentOptionActive : ""}`}
                onClick={() => setPaymentMethod("webxpay")}
              >
                <div className={styles.paymentRadio}>
                  <div className={paymentMethod === "webxpay" ? styles.radioFilled : styles.radioEmpty} />
                </div>
                <div className={styles.paymentInfo}>
                  <p className={styles.paymentName}>Pay with Card</p>
                  <div className={styles.cardLogos}><img src="/visa-logo.avif" alt="Visa" style={{height:22,width:"auto"}} /><img src="/mastercard-logo.png" alt="Mastercard" style={{height:22,width:"auto"}} /><span className={styles.paymentDesc}>via WebXPay</span></div>
                </div>
                
              </div>

              {/* Mintpay — hidden while a voucher is applied */}
              {mintpayAvailable && (
                <div
                  className={`${styles.paymentOption} ${paymentMethod === "mintpay" ? styles.paymentOptionActive : ""}`}
                  onClick={() => setPaymentMethod("mintpay")}
                >
                  <div className={styles.paymentRadio}>
                    <div className={paymentMethod === "mintpay" ? styles.radioFilled : styles.radioEmpty} />
                  </div>
                  <div className={styles.paymentInfo}>
                    <div className={styles.mintpayOptionHeader}>
                      <p className={styles.paymentName}>Pay in 3 installments with</p>
                      <MintpayLogo height={14} />
                    </div>
                    <p className={styles.paymentDesc}>
                      3 × LKR {installment} over 3 months
                    </p>
                  </div>
                </div>
              )}

              {/* Say why the installment option went away, so it doesn't
                  look like the page is broken. */}
              {!mintpayAvailable && (
                <p className={styles.paymentNote}>
                  Installment payments aren't available on gift voucher orders.
                  Remove the voucher to pay with <MintpayLogo height={12} />.
                </p>
              )}
            </div>

            {/* Gift voucher */}
            <div className={styles.voucherBox}>
              {voucher ? (
                <div className={styles.voucherApplied}>
                  <div>
                    <p className={styles.voucherAppliedCode}>🎁 {voucher.code}</p>
                    <p className={styles.voucherAppliedAmount}>
                      LKR {discount.toLocaleString()} off
                    </p>
                  </div>
                  <button type="button" className={styles.voucherRemove} onClick={removeVoucher}>
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <label className={styles.voucherLabel}>Gift Voucher</label>
                  <div className={styles.voucherRow}>
                    <input
                      className={styles.voucherInput}
                      placeholder="Enter voucher code"
                      value={voucherInput}
                      onChange={(e) => { setVoucherInput(e.target.value); setVoucherError(""); }}
                      autoCorrect="off"
                      spellCheck="false"
                    />
                    <button
                      type="button"
                      className={styles.voucherBtn}
                      onClick={applyVoucher}
                      disabled={checkingVoucher}
                    >
                      {checkingVoucher ? "Checking…" : "Apply"}
                    </button>
                  </div>
                  {voucherError && <p className={styles.voucherError}>{voucherError}</p>}
                </>
              )}
            </div>

            {/* Pay button */}
            <button type="submit" className={styles.payBtn} disabled={paying}>
              {paying ? (
                "Processing..."
              ) : paymentMethod === "mintpay" && mintpayAvailable ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  Pay 3 × LKR {installment} with <MintpayLogo height={18} />
                </span>
              ) : (
                `Pay LKR ${payableTotal.toLocaleString()} via WebXPay`
              )}
            </button>
            <p className={styles.secure}>🔒 SSL Encrypted · Secure Checkout</p>
          </form>

          {/* Order Summary */}
          <div className={styles.summary}>
            <h2 className={styles.sectionTitle}>Order Summary</h2>
            <div className={styles.summaryItems}>
              {items.map((item) => (
                <div key={item.id} className={styles.summaryItem}>
                  {/\.(png|jpg|jpeg|webp)/i.test(item.src)
                    ? <img src={item.src} alt={item.name} className={styles.thumb} style={{ objectFit: "contain", background: "#f5ede4" }} />
                    : <video src={item.src} className={styles.thumb} muted playsInline preload="metadata" />
                  }
                  <div className={styles.summaryItemInfo}>
                    <p className={styles.summaryItemName}>{item.name}</p>
                    <p className={styles.summaryItemGem}>{item.details.gemstone} · {item.details.carat}</p>
                    <p className={styles.summaryItemPrice}>LKR {item.price.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.summaryFooter}>
              <div className={styles.summaryRow}>
                <span>Subtotal</span>
                <span>LKR {total.toLocaleString()}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Delivery</span>
                <span className={styles.free}>FREE</span>
              </div>
              {discount > 0 && (
                <div className={styles.summaryRow}>
                  <span>Voucher {voucher.code}</span>
                  <span className={styles.discountAmount}>− LKR {discount.toLocaleString()}</span>
                </div>
              )}
              <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                <span>Total</span>
                <span>LKR {payableTotal.toLocaleString()}</span>
              </div>
            </div>

            {/* Mintpay breakdown */}
            {paymentMethod === "mintpay" && mintpayAvailable && (
              <div className={styles.mintpayBreakdown}>
                <div className={styles.mintpayBreakdownHeader}>
                  <span className={styles.mintpayTitle}>Installment Plan with</span>
                  <MintpayLogo height={16} />
                </div>
                {[1, 2, 3].map((n) => (
                  <div key={n} className={styles.mintpayRow}>
                    <span>Installment {n}</span>
                    <span>LKR {installment}</span>
                  </div>
                ))}
                <p className={styles.mintpayNote}>0% interest over 2 months</p>
              </div>
            )}

            <div className={styles.trustRow}>
              <div className={styles.trustBadge}>🏆 Certified</div>
              <div className={styles.trustBadge}>💎 Lifetime Warranty</div>
              <div className={styles.trustBadge}>🚚 Free Delivery</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}